// The head of a rebuilt page (direction B): breadcrumbs, title, description, actions on the right,
// tabs underneath. For the route PRs after the shell; old pages keep their own headers.
//
// Usage:
//   <PageShell>
//     <PageHeader
//       breadcrumbs={
//         <Breadcrumb><BreadcrumbList>
//           <BreadcrumbItem>{t('menu.kepabeanan')}</BreadcrumbItem>
//         </BreadcrumbList></Breadcrumb>
//       }
//       title={t('menu.docPabean')}
//       description={t('…')}
//       meta={<Badge variant="info">BC 2.0</Badge>}
//       actions={<Button>{t('menu.buatAju')}</Button>}
//       tabs={<Tabs value={tab} onValueChange={setTab}><TabsList variant="line">…</TabsList></Tabs>}
//     />
//     …page body…
//   </PageShell>
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** A Lumen `<Breadcrumb>`; the current page is the title, so leave it out of the trail. */
  breadcrumbs?: ReactNode;
  /** Beside the title: a status badge, a mono identifier. */
  meta?: ReactNode;
  /** Right-aligned buttons; they wrap under the title on narrow screens. */
  actions?: ReactNode;
  /** Under the head, full width: a Lumen `<Tabs>` with `TabsList variant="line"`. */
  tabs?: ReactNode;
  className?: string;
}

export function PageHeader({ title, description, breadcrumbs, meta, actions, tabs, className }: PageHeaderProps) {
  return (
    <header data-slot="page-header" className={cn('flex flex-col gap-4', className)}>
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          {breadcrumbs}
          <div className="flex min-w-0 flex-wrap items-center gap-2.5">
            <h1 className="font-bold text-2xl text-fg leading-tight tracking-tight">{title}</h1>
            {meta}
          </div>
          {description && <p className="max-w-3xl text-13 text-fg-2">{description}</p>}
        </div>
        {actions && <div className="flex min-w-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {tabs}
    </header>
  );
}

/**
 * The content column of a rebuilt page: padding on top of the frame's own (old pages fit the frame
 * as they are), and the vertical rhythm between header, cards and tables.
 */
export function PageShell({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div data-slot="page-shell" className={cn('flex flex-col gap-5 px-1.5 pt-1 pb-8 sm:px-3.5', className)}>
      {children}
    </div>
  );
}
